import { AloftDate } from '@aloft/toolkit-shared';
import { Flex } from '@aloft/toolkit-sui';

import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  TrendsChartDataType,
  getHousingTrendsChartData,
} from '../utils/getHousingTrendsChartData.ts';
import { ZillowHomeValueIndexTrendsChartTooltip } from './ZillowHomeValueIndexTrendsChartTooltip.tsx';
import { ZillowHomeValueIndexTrendsData } from './types.ts';

const getFontSize = (reduceFontSize: boolean): number | undefined =>
  reduceFontSize ? 13 : undefined;

type Props = {
  isZillow: boolean;
  data: TrendsChartDataType[];
  effectiveDate: AloftDate;
  disableAnimations?: boolean;

  updatedAt?: AloftDate;
  reduceFontSize?: boolean;
  width?: number;
  height?: number;
};

const Chart = ({
  isZillow,
  data,
  effectiveDate,
  disableAnimations = false,
  updatedAt,
  reduceFontSize = false,
  width,
  height,
}: Props) => (
  <LineChart
    data={data}
    margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
    width={width}
    height={height}
  >
    <CartesianGrid vertical={!isZillow} />
    <XAxis dataKey="month" interval={1} tickMargin={8} fontSize={getFontSize(reduceFontSize)}>
      <Label
        fontSize={getFontSize(reduceFontSize)}
        value={`Effective Date: ${effectiveDate.format('MMM dd, yyyy')}`}
        position="insideBottomLeft"
        offset={-20}
      />
      {updatedAt && (
        <Label
          fontSize={getFontSize(reduceFontSize)}
          value={`Data retrieved ${updatedAt.format('MMM dd, yyyy')}`}
          position="insideBottomRight"
          offset={-20}
        />
      )}
    </XAxis>
    <YAxis
      tickMargin={8}
      fontSize={getFontSize(reduceFontSize)}
      tickFormatter={(val) => `$${val / 1000}k`}
      domain={([dataMin, dataMax]) => {
        const roundedMin = Math.floor(dataMin / 100_000) * 100_000;
        const roundedMax = Math.ceil(dataMax / 100_000) * 100_000;
        return [roundedMin, roundedMax];
      }}
    />
    {!disableAnimations && (
      /* @ts-expect-error IDK what recharts is up to magically injecting props but */
      <Tooltip content={<ZillowHomeValueIndexTrendsChartTooltip />} />
    )}
    <Legend />
    <Line
      type="monotone"
      dataKey="historical"
      name={isZillow ? 'Typical Home Value' : 'Median Close Price'}
      isAnimationActive={!disableAnimations}
    />
    <Line
      dataKey="projection"
      name="Projection"
      stroke="#C05621"
      strokeDasharray="3 3"
      isAnimationActive={!disableAnimations}
    />
    {data.map(
      (x) =>
        x.comps && (
          <ReferenceLine key={x.month} x={x.month} stroke="red" strokeDasharray="3 3">
            <Label value={x.comps} position="insideBottomLeft" fontSize="70%" />
          </ReferenceLine>
        ),
    )}
  </LineChart>
);

export const ZillowHomeValueIndexTrendsChartUnwrapped = (props: Props) => {
  const { width, height, ...chartProps } = props;

  if (width && height) return <Chart {...chartProps} width={width} height={height} />;

  return (
    <ResponsiveContainer width="100%" height={350}>
      <Chart {...chartProps} />
    </ResponsiveContainer>
  );
};

export const ZillowHomeValueIndexTrendsChart = ({
  updatedAt,
  ...rest
}: ZillowHomeValueIndexTrendsData & { updatedAt?: AloftDate }) => {
  const data = getHousingTrendsChartData(rest);
  return (
    <Flex
      backgroundColor="textOffset"
      flex="1 1"
      flexDirection="column"
      borderColor="surface4"
      borderWidth="1px"
      borderRadius="4px"
      boxShadow="0px 4px 6px -1px #0000001A"
    >
      <ZillowHomeValueIndexTrendsChartUnwrapped
        isZillow={rest.isZillow}
        data={data}
        effectiveDate={rest.effectiveDate}
        updatedAt={updatedAt}
      />
    </Flex>
  );
};
